import { Row, Items, isRepeater, isCellProperty } from 'src/components/Repeater';
import type { CellProperties } from 'src/components/Repeater/types';

const getValues = (items: Items[]): PropertyValue[] => {
  const result: PropertyValue[] = [];

  for (const item of items) {
    const mappedItem = Object.values(item).reduce<PropertyValue>((acc, row) => {
      if (isRepeater(row)) {
        acc[row.id] = getValues(row.items);
      } else {
        acc[row.id] = row.value;
      }
      return acc;
    }, {});

    result.push(mappedItem);
  }

  return result;
};

const getRepeaterItems = (
  property: RepeaterProperties,
  propertyValues?: PropertyValue[]
): Items[] => {
  const result: Items[] = [];
  if (propertyValues?.length) {
    for (const propertyValue of propertyValues) {
      const items: Items = {};
      for (const child of property.children.filter(isCellProperty)) {
        items[child.id] = getRepeaterItem(child, propertyValue);
      }
      result.push(items);
    }
  } else {
    const item: Items = {};
    for (const child of property.children.filter(isCellProperty)) {
      item[child.id] = getRepeaterItem(child);
    }
    result.push(item);
  }
  return result;
};

function getRepeaterItem(child: CellProperties, propertyValue?: PropertyValue): Row {
  switch (child.type) {
    case 'repeater': {
      const values = propertyValue ? (propertyValue[child.id] as PropertyValue[]) : [];
      return {
        ...child,
        name: child.name,
        id: child.id,
        items: getRepeaterItems(child, values),
      };
    }
    case 'select':
      return {
        ...child,
        id: child.id,
        type: child.type,
        value: propertyValue ? (propertyValue[child.id] as string) : (child.defaultValue ?? ''),
        options: child.options,
      };
    case 'url':
    case 'text':
      return {
        ...child,
        id: child.id,
        type: child.type,
        value: propertyValue ? (propertyValue[child.id] as string) : (child.defaultValue ?? ''),
        name: child.name,
      };
    default:
      return {
        ...child,
        id: child.id,
        type: child.type,
        value: propertyValue ? (propertyValue[child.id] as string) : (child.defaultValue ?? ''),
      };
  }
}

/**
 * Transforms imported sport data from name-based keys to ID-based keys.
 * Used for Editor graphics where property IDs are UUIDs and names contain legacy references.
 * Should only be called for Editor graphics, not legacy graphics.
 */
const transformSportDataValue = (
  value: string | PropertyValue[],
  property: Properties
): string | PropertyValue[] => {
  // Non-array values don't need transformation
  if (!Array.isArray(value)) {
    return value;
  }

  // Only repeater properties have children to transform
  if (property.type !== 'repeater' || !property.children) {
    return value;
  }

  // Create lookup map for property name → ID conversion
  const childPropertyByName = new Map(
    property.children.map((child: Properties) => [child.name, child])
  );

  // Transform each repeater item
  return value.map((dataItem) =>
    Object.entries(dataItem).reduce<PropertyValue>((acc, [propertyName, propertyValue]) => {
      const childProperty = childPropertyByName.get(propertyName);

      if (childProperty) {
        if (childProperty.type === 'repeater' && Array.isArray(propertyValue)) {
          // Recursively transform nested repeaters
          acc[childProperty.id] = transformSportDataValue(propertyValue, childProperty);
        } else {
          acc[childProperty.id] = propertyValue as string | number | PropertyValue[];
        }
      }
      return acc;
    }, {})
  );
};

export { getRepeaterItems, getValues, transformSportDataValue };
